import type { SendEventParams } from '~/shared/analytix'
import type { PlayerOpenParameters } from './player-open'

import { Events } from '@setplex/pureya'
import { createWatch } from 'effector'
import { model as mq, PLAYER } from '~/shared/mq'
import { SnapshotMap } from '../../general/snapshot-map'
import { playStart, playStop } from '../models/play-start-stop'
import { transmittedBytes } from '../models/stream-info'
import { parameters as playerOpenParameters } from './player-open'

const SNAPSHOTS_TO_KEEP = 1

export type StreamInfoParameters = SendEventParams.StreamInfo & {
  '(playerId)'?: string[] // auxiliary parameter to store players id for internal use, it will not be sent to analytix
  '(traffic)'?: Map<string, Events.Media.TransmittedBytesInfo> // auxiliary parameter to store traffic info per player
}

/**
 * SnapshotMap to store `stream_info` event parameters with their history
 */
export const parameters = new SnapshotMap<StreamInfoParameters>(
  SNAPSHOTS_TO_KEEP
)

//
// Update parameters
//

function fromPlayerOpenParameters(params: SnapshotMap<PlayerOpenParameters>) {
  parameters
    .update('(playerId)', params.get('(playerId)'))
    .update('contentId', params.get('contentId'))
    .update('contentType', params.get('contentType'))
    .update('contentUrl', params.get('contentUrl'))
    .update('contentName', params.get('contentName'))
    .update('isContentPaid', params.get('isContentPaid'))
    .commit()
    .notify()
}

function fromPlayerState(snapshot: {
  playerId: string
  videoQuality: string
  playbackSessionId: string
}) {
  parameters
    .update('(playerId)', [snapshot.playerId])
    .update('videoQuality', snapshot.videoQuality)
    .update('playbackSessionId', snapshot.playbackSessionId)
    .update('bufferState', 'full')
    .commit()
    .notify()
}

function fromBuffering() {
  parameters.set('bufferState', 'empty').notify()
}

function fromStreamTrafficUpdated({
  playerId,
  payload,
}: {
  playerId: string
  payload: Events.Media.TransmittedBytesInfo
}) {
  const trafficMap = parameters.get('(traffic)') ?? new Map()
  let { bytes = 0, bandwidth = 0 } = trafficMap.get(playerId) ?? {}

  bytes += payload.bytes
  bandwidth = payload.bandwidth

  trafficMap.set(playerId, { bytes, bandwidth })
  parameters.set('(traffic)', trafficMap).notify()
}

function fromNewSource(playerId: string) {
  const trafficMap = parameters.get('(traffic)') ?? new Map()
  trafficMap.set(playerId, { bytes: 0, bandwidth: 0 })
  parameters.set('(traffic)', trafficMap).notify()
}

//
// Updates listeners
//

// on player open parameters update
playerOpenParameters.updates.on(() => {
  fromPlayerOpenParameters(playerOpenParameters)
})

// on player state update
createWatch({
  unit: playStart,
  fn: ({ snapshot }) => {
    fromPlayerState(snapshot)
  },
})

// on stop because of buffering
createWatch({
  unit: playStop,
  fn: ({ reason }) => {
    if (reason === 'streamBuffering') {
      fromBuffering()
    }
  },
})

// on stream traffic update
createWatch({
  unit: transmittedBytes,
  fn: ({ playerId, payload }) => {
    fromStreamTrafficUpdated({ playerId, payload })
  },
})

// from new source loading
createWatch({
  unit: mq.on<{ playerId: string }>(`${PLAYER}/${Events.Media.Load}`),
  fn({ playerId }) {
    fromNewSource(playerId)
  },
})
